import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Slider from "react-slick";
import { RiStarFill } from "react-icons/ri";
import { HiArrowRight, HiArrowLeft } from "react-icons/hi";
import Title from '../layouts/Title';
import quote from "../../assets/images/testmonial/quote.png";
import defaultUserImage from "../../assets/images/testmonial/Users User Female.png";
import { useTranslation } from 'react-i18next';
import { fetchTestimonials } from '../../api';
import { queryKeys } from '../../api/keys';

function SampleNextArrow(props) {
  const { onClick } = props;
  return (
    <div className="w-14 h-12 bg-[#0c1821] hover:bg-black duration-300 rounded-md text-2xl text-gray-400 flex justify-center items-center absolute top-0 right-0 shadow-shadowOne cursor-pointer z-10" onClick={onClick}>
      <HiArrowRight />
    </div>
  );
}

function SamplePrevArrow(props) {
  const { onClick } = props;
  return (
    <div className="w-14 h-12 bg-[#0c1821] hover:bg-black duration-300 rounded-md text-2xl text-gray-400 flex justify-center items-center absolute top-0 right-20 shadow-shadowOne cursor-pointer z-10" onClick={onClick}>
      <HiArrowLeft />
    </div>
  );
}

const Testimonial = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [dotActive, setDocActive] = useState(0);

  const { data: testimonials, isLoading, error } = useQuery({
    queryKey: queryKeys.testimonials(currentLang),
    queryFn: () => fetchTestimonials(currentLang),
  });

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    beforeChange: (prev, next) => setDocActive(next),
    appendDots: (dots) => (
      <div style={{ borderRadius: "10px", padding: "10px" }}>
        <ul style={{ display: "flex", gap: "15px", justifyContent: "center", marginTop: "20px" }}>
          {dots}
        </ul>
      </div>
    ),
    customPaging: (i) => (
      <div style={i === dotActive ? { width: "12px", height: "12px", background: "#ff014f", borderRadius: "50%", cursor: "pointer" } : { width: "12px", height: "12px", background: "gray", borderRadius: "50%", cursor: "pointer" }}></div>
    ),
  };

  const numberOfRating = (number) => {
    return Array.from({ length: number }, (_, i) => <RiStarFill key={i} />);
  };

  if (isLoading) {
    return (
      <section className="w-full py-20 border-b-[1px] border-b-black">
        <div className="flex justify-center items-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-designColor"></div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full py-20 border-b-[1px] border-b-black">
        <p className="text-red-500 text-center">Error loading testimonials: {error.message}</p>
      </section>
    );
  }

  const approvedTestimonials = testimonials.filter(t => t.is_approved);

  if (approvedTestimonials.length === 0) {
    return null;
  }

  return (
    <section className={`w-full py-20 border-b-[1px] border-b-black ${i18n.language === 'ar' ? "ar-dir" : "en-dir"}`}>
      <div className="flex justify-center items-center text-center">
        <Title title={`${t("tesimonial.feature")}`} des={`${t("tesimonial.title")}`} />
      </div>
      <div className="max-w-6xl mx-auto">
        <Slider {...settings}>
          {approvedTestimonials.map((testimonial) => (
            <div key={testimonial.id} className="w-full">
              <div className="w-full h-auto flex flex-col lgl:flex-row justify-between">
                <div className="w-full lgl:w-[35%] h-full bg-gradient-to-r from-[#1e2024] to-[#23272b] p-8 rounded-lg shadow-shadowOne flex flex-col md:flex-row lgl:flex-col gap-8 justify-center md:justify-start lgl:justify-center">
                  <img
                    className="h-72 md:h-32 lgl:h-72 rounded-lg object-cover"
                    src={testimonial.client_image_url || defaultUserImage}
                    alt={testimonial.client_name || "Client"}
                  />
                  <div className="w-full flex flex-col justify-end">
                    <p className="text-xs uppercase text-designColor tracking-wide mb-2">
                      {testimonial.client_position || ""}
                    </p>
                    <h3 className="text-2xl font-bold">
                      {testimonial.client_name || "Anonymous"}
                    </h3>
                    <p className="text-base tracking-wide text-gray-500">
                      {testimonial.client_location || ""}
                    </p>
                  </div>
                </div>
                <div className="w-full lgl:w-[60%] h-full flex flex-col justify-between">
                  <img className="w-20 lgl:w-32" src={quote} alt="quote" />
                  <div className="w-full h-[70%] py-10 bg-gradient-to-r from-[#1e2024] to-[#23272b] rounded-lg shadow-shadowOne p-4 lgl:p-8 flex flex-col justify-center gap-4 lgl:gap-8">
                    <div className="flex flex-col justify-between lgl:items-center py-6 border-b-2 border-b-gray-900">
                      <div>
                        <h3 className="text-xl lgl:text-2xl font-medium tracking-wide">
                          {testimonial.service_name || "Service"}
                        </h3>
                        <p className="text-base text-center text-gray-400 mt-3 mb-3">
                          {testimonial.project_start_date && (
                            <>
                              {t("tesimonial.clients.client1.from")} <span className='text-designColor ml-2 mr-2'>{testimonial.project_start_date}</span>
                              {t("tesimonial.clients.client1.to")} <span className='text-designColor ml-2 mr-2'>{testimonial.project_end_date}</span>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="text-yellow-500 flex gap-1">
                        {numberOfRating(testimonial.rating || 5)}
                      </div>
                    </div>
                    <p className="text-base font-titleFont text-gray-400 font-medium tracking-wide leading-6">
                      {testimonial.message}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};

export default Testimonial;